import Link from "next/link"
import { VelovaLogo } from "@/components/velova-logo"
import "./footer-section.css"

/**
 * Homepage footer — port of the Claude Design export `Footer Section.dc.html`
 * (# Velova Actions Section (1)). The export is a design-doc canvas showing
 * three fixed-width mockups; this ports the real <footer> once and lets CSS
 * make it fluid across the desktop / tablet / mobile breakpoints.
 *
 * Static markup + one CSS signal sweep on the top hairline — no hooks, so it
 * renders as a plain component. Styling + fonts live in ./footer-section.css.
 *
 * This is the homepage-only footer; the shared cross-route footer
 * (components/landing/site-footer.tsx, used by ~12 other routes) is untouched.
 *
 * Link destinations use real routes/anchors. Two export items with no real
 * destination were dropped rather than fabricated: the "LinkedIn" company
 * link (no known URL) and the "Cookies" legal link (no cookies page). Add a
 * LinkedIn URL / cookies route and they can be wired back in.
 */

type FooterLink = { label: string; href: string; accent?: boolean }
type FooterCol = { heading: string; links: FooterLink[] }

// Two groups only: real PAGES we keep, and anchors to the sections that
// actually exist on the home route (`app/page.tsx`). Everything else was
// cleared per request.
const COLUMNS: FooterCol[] = [
  {
    heading: "Pages",
    links: [
      { label: "Actions", href: "/actions" },
      { label: "Pricing", href: "/pricing" },
      { label: "Contact", href: "/contact" },
      { label: "Sign up", href: "/signup", accent: true },
    ],
  },
  {
    heading: "On this page",
    links: [
      { label: "AI models", href: "#coverage" },
      { label: "How it works", href: "#method" },
      { label: "Features", href: "#features" },
    ],
  },
]

const LEGAL: FooterLink[] = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
]

function FooterLinkEl({ link }: { link: FooterLink }) {
  const cls = `vf-link${link.accent ? " vf-link-accent" : ""}`
  // route paths get client-side nav; same-page anchors stay plain <a>
  return link.href.startsWith("/") ? (
    <Link href={link.href} className={cls}>
      {link.label}
    </Link>
  ) : (
    <a href={link.href} className={cls}>
      {link.label}
    </a>
  )
}

export function FooterSection() {
  return (
    <footer className="velova-footer">
      <div aria-hidden className="vf-glow" />
      <div aria-hidden className="vf-grid" />

      {/* vertical connector out of the CTA */}
      <div aria-hidden className="vf-connector">
        <div className="vf-connector-line">
          <span className="vf-connector-dot" />
        </div>
      </div>

      <div className="vf-inner">
        <span aria-hidden className="vf-sweepwrap">
          <span className="vf-sweep" />
        </span>

        <div className="vf-top">
          <div className="vf-brand">
            {/* The real brand asset (/logo/velova-light.png via VelovaLogo),
                not the CSS bars + text span this used to draw. `light` because
                this footer is always dark. */}
            <div className="vf-logo">
              <VelovaLogo variant="light" width={176} />
            </div>
            <div className="vf-tagline">See how AI presents your brand—and what to improve next.</div>
            <div className="vf-caps">AI visibility · Competitor intelligence · Evidence-backed Actions</div>
          </div>

          <div className="vf-nav">
            {COLUMNS.map((col) => (
              <nav key={col.heading} className="vf-col" aria-label={col.heading}>
                <div className="vf-col-head">{col.heading}</div>
                <div className="vf-col-links">
                  {col.links.map((link) => (
                    <FooterLinkEl key={link.label} link={link} />
                  ))}
                </div>
              </nav>
            ))}
          </div>
        </div>

        <div className="vf-bottom">
          <span className="vf-copy">© 2026 Velova. All rights reserved.</span>
          <span className="vf-legal-group">
            {LEGAL.map((link) => (
              <Link key={link.label} href={link.href} className="vf-legal">
                {link.label}
              </Link>
            ))}
            <a href="#main-content" className="vf-top-btn">
              Back to top
              <span aria-hidden className="vf-top-arrow">↑</span>
            </a>
          </span>
        </div>
      </div>
    </footer>
  )
}
